import { MutableRefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { getNearestScrollElement, isElementClippingScroll } from '../model';

/**
 * Sets the scroll parent when the list node is committed, before the next paint.
 * Until then the parent stays null so the window can render no rows.
 *
 * `layoutSignal` (e.g. the reserved list height) re-checks the parent only when
 * the current one stopped clipping, so the virtualizer is not re-bound on
 * every expand or measurement.
 */
export function useAttachedScrollParent(layoutSignal: number): {
  listRef: MutableRefObject<HTMLDivElement | null>;
  attachList: (node: HTMLDivElement | null) => void;
  scrollParent: HTMLElement | null;
} {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const attachList = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    setScrollParent(node ? getNearestScrollElement(node) : null);
  }, []);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || (scrollParent && isElementClippingScroll(scrollParent))) {
      return;
    }
    setScrollParent(getNearestScrollElement(list));
  }, [layoutSignal, scrollParent]);

  return { listRef, attachList, scrollParent };
}
